import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { brandCache } from "@/lib/brand";
import { Btn } from "@/components/site/Buttons";
import { SearchBar } from "@/components/site/SearchBar";
import { Menu, X, Search } from "lucide-react";

const FALLBACK = [
  { label: "Organizza il tuo viaggio", link: "/organizza-il-tuo-viaggio" },
  { label: "Itinerari testati", link: "/itinerari-testati" },
  { label: "Budget reali e simulazioni", link: "/budget" },
  { label: "Tips, Tool e Risorse", link: "/risorse" },
  { label: "Guide PDF", link: "/guide" },
  { label: "Blog", link: "/blog" },
];

function NavItem({ it, className, testid }) {
  const external = it.external || (it.link || "").startsWith("http");
  if (external) {
    return <a href={it.link} target={it.new_tab ? "_blank" : undefined} rel="noreferrer" className={className} data-testid={testid}>{it.label}</a>;
  }
  return <Link to={it.link} target={it.new_tab ? "_blank" : undefined} className={className} data-testid={testid}>{it.label}</Link>;
}

export function Header() {
  const [items, setItems] = useState(FALLBACK);
  const [brand, setBrand] = useState({ name: brandCache.brand_name, logo: brandCache.logo, cta_label: brandCache.default_cta_label, cta_link: brandCache.default_cta_link });
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/settings/menu").then((r) => {
      const its = (r.data?.data?.items || []).filter((i) => !i.hidden);
      if (its.length) setItems(its);
    }).catch(() => {});
    api.get("/settings/brand").then((r) => {
      const b = r.data?.data;
      if (b) setBrand({ name: b.brand_name || brandCache.brand_name, logo: b.logo || "", cta_label: b.default_cta_label || brandCache.default_cta_label, cta_link: b.default_cta_link || brandCache.default_cta_link });
    }).catch(() => {});
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const logoEl = brand.logo
    ? <img src={brand.logo} alt={brand.name} className="h-12 md:h-14 w-auto object-contain" data-testid="logo-image" />
    : <span className="font-logo text-3xl md:text-[2rem] text-vs-orange leading-none">{brand.name}</span>;

  return (
    <header className="sticky top-0 z-50 bg-vs-bg/85 backdrop-blur-md border-b border-vs-text/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between gap-4">
        <Link to="/" className="shrink-0" data-testid="logo-link">{logoEl}</Link>

        <nav className="hidden xl:flex items-center gap-6 flex-1 justify-center">
          {items.map((it, i) => (
            <NavItem key={`${it.link}-${i}`} it={it}
              className="text-sm font-bold text-vs-text/80 hover:text-vs-orange transition-colors whitespace-nowrap"
              testid={`nav-link-${it.link}`} />
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setSearchOpen(true)}
            className="p-2.5 rounded-full hover:bg-vs-peach/50 transition-colors"
            aria-label="Cerca"
            data-testid="search-trigger"
          >
            <Search className="w-5 h-5 text-vs-green" strokeWidth={1.5} />
          </button>
          <div className="hidden lg:block">
            <Btn to={brand.cta_link} className="px-5 py-2.5 text-sm" data-testid="header-cta" data-track-cta={`Header: ${brand.cta_label}`}>
              {brand.cta_label}
            </Btn>
          </div>
          <button
            className="xl:hidden p-2.5 rounded-full hover:bg-vs-peach/50"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
            data-testid="mobile-menu-trigger"
          >
            {open ? <X className="w-6 h-6 text-vs-text" /> : <Menu className="w-6 h-6 text-vs-text" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-vs-bg border-t border-vs-text/10 px-5 py-4" id="mobile-menu" data-testid="mobile-menu">
          <nav className="flex flex-col gap-1">
            {items.map((it, i) => (
              <NavItem key={`${it.link}-${i}`} it={it}
                className="py-3 font-bold text-vs-text border-b border-vs-text/5"
                testid={`mobile-nav-${it.link}`} />
            ))}
          </nav>
          <div className="mt-4">
            <Btn to={brand.cta_link} className="w-full">{brand.cta_label}</Btn>
          </div>
        </div>
      )}

      {searchOpen && <SearchBar onClose={() => setSearchOpen(false)} />}
    </header>
  );
}
